import { useState } from "react";

import { ActionIcon, Button, Flex, Group, Modal, Text } from "@mantine/core";
import { useForm } from "@mantine/form";

import { IconX } from "@tabler/icons-react";
import isEqual from "lodash/isEqual";

import {
  ProceduresPickerEditTarget,
  StagedProceduresOperation,
} from "@/encounters/types/procedures";
import { buildStagedProceduresOperation } from "@/encounters/utils/staged-procedures";
import {
  getProceduresPickerInitialValues,
  ProceduresPickerFormType,
  proceduresPickerInitialValues,
} from "@/encounters/zod-schemas/procedures";
import { CptCopyrightNotice } from "@/shared/cpt-copyright";
import type { ServiceDateKey } from "@/shared/procedure-code-effective-dates";
import { toast } from "@/utils/notifications";

import ProceduresPickerLeftPanel from "./ProceduresPickerLeftPanel";
import ProceduresPickerRightPanel from "./ProceduresPickerRightPanel";

/**
 * What the picker is about to do. The button label and the toast are both built from this value,
 * so they always describe the same action.
 *
 * `add` puts new diagnoses and procedures on the encounter, and `addDiagnoses` puts diagnoses on it
 * with no procedure under them. `update` changes the procedures under an edited diagnosis.
 * `updateProcedureCode` changes the diagnoses one locked procedure code is billed under.
 *
 * Each message tells the provider that the picker only stages the work, and names the save that
 * writes it.
 */
type SaveIntent = "add" | "addDiagnoses" | "update" | "updateProcedureCode";

/**
 * What the provider has to do for the staged work to reach the server. The procedures tab is
 * written by the encounter's own save. The billing tab's biller section is written by its own Save
 * Billing Info button. The toast has to name whichever one applies, so the provider knows which
 * button to press.
 */
const DEFAULT_STAGED_SAVE_LABEL = "save the encounter";

const buildSaveCopy = (
  stagedSaveLabel: string,
): Record<SaveIntent, { buttonLabel: string; successTitle: string; successMessage: string }> => ({
  add: {
    buttonLabel: "Add to Encounter",
    successTitle: "Procedures added to encounter",
    successMessage: `The procedures will be saved when you ${stagedSaveLabel}`,
  },
  addDiagnoses: {
    buttonLabel: "Add to Encounter",
    successTitle: "Diagnoses added to encounter",
    successMessage: `The diagnoses will be saved when you ${stagedSaveLabel}`,
  },
  update: {
    buttonLabel: "Save Changes",
    successTitle: "Changes applied",
    successMessage: `The procedures will be updated when you ${stagedSaveLabel}`,
  },
  updateProcedureCode: {
    buttonLabel: "Save Changes",
    successTitle: "Changes applied",
    successMessage: `The procedure code will be updated when you ${stagedSaveLabel}`,
  },
});

/**
 * The picker's own heading.
 *
 * A procedure-locked session only changes what one code is billed under, so it says so. Every
 * other session can add and remove both diagnoses and procedures, so it uses wording that covers
 * both sides.
 */
const buildPickerTitle = (editTarget: ProceduresPickerEditTarget | null): string =>
  editTarget?.lockedSide === "procedure" ? "Edit Procedure Code" : "Add Diagnoses & Procedures";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ProceduresPickerProps {
  opened: boolean;
  onClose: () => void;
  /**
   * What this session was opened to edit and which side it locks, or null to add new diagnoses and
   * procedures to the encounter.
   */
  editTarget: ProceduresPickerEditTarget | null;
  /**
   * Hands the finished session to the tab, which holds it until the encounter is saved. The picker
   * writes nothing itself. Procedures and diagnoses reach the server only with the encounter's own
   * save.
   */
  onStage: (operation: StagedProceduresOperation) => void;
  /** Named in the toast as what writes the staged work. Defaults to the encounter's own save. */
  stagedSaveLabel?: string;
  /**
   * Whether adding needs a procedure as well as a diagnosis. The biller section sets it: its rows
   * are one per procedure code, so a diagnosis added on its own would show on none of them.
   */
  requiresProcedure?: boolean;
  /**
   * The encounter's service date (`yyyy-MM-dd`). Codes not billable on it are not offered, and
   * codes not yet billable on it show a warning. Without it no date rule applies.
   */
  serviceDate?: ServiceDateKey;
}

export function ProceduresPicker({
  opened,
  onClose,
  editTarget,
  onStage,
  stagedSaveLabel,
  requiresProcedure,
  serviceDate,
}: ProceduresPickerProps) {
  return (
    <Modal
      fullScreen
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      styles={{
        body: {
          height: "calc(100dvh - 60px)",
          display: "flex",
          flexDirection: "column",
          padding: "16px 24px",
        },
        header: { padding: "12px 24px", backgroundColor: "var(--mantine-color-gray-0)" },
      }}
      title={
        <Group align="center" justify="space-between">
          <ActionIcon
            size="md"
            onClick={onClose}
            variant="transparent"
            data-testid="procedures-picker-close-button"
          >
            <IconX size={24} />
          </ActionIcon>
          <Text fz="lg" fw={600}>
            {buildPickerTitle(editTarget)}
          </Text>
        </Group>
      }
    >
      {/* Mantine leaves modal children unmounted while closed. The content below, and the form it
          owns, mount fresh on every open, so there is nothing to sync against the caller. */}
      <ProceduresPickerContent
        onClose={onClose}
        onStage={onStage}
        editTarget={editTarget}
        serviceDate={serviceDate}
        stagedSaveLabel={stagedSaveLabel}
        requiresProcedure={requiresProcedure}
      />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal content
// ---------------------------------------------------------------------------

function ProceduresPickerContent({
  onClose,
  editTarget,
  onStage,
  stagedSaveLabel = DEFAULT_STAGED_SAVE_LABEL,
  requiresProcedure = false,
  serviceDate,
}: Omit<ProceduresPickerProps, "opened">) {
  // Editing a diagnosis opens on one diagnosis and locks it. The provider changes the procedures
  // underneath, down to none at all, and cannot search for, add, or remove diagnoses. Removing the
  // diagnosis itself is the card's Delete action.
  const isEditingDiagnosis = editTarget?.lockedSide === "diagnosis";

  // Editing a procedure code works the other way around, and is how the biller section edits a
  // row. The code is locked, and the biller adds and removes the diagnoses it is billed under.
  const isEditingProcedureCode = editTarget?.lockedSide === "procedure";

  // Captured once on mount. The save also compares against this snapshot to decide which encounter
  // resources this session is allowed to touch.
  //
  // The procedure-locked path clones its target. That target arrives already in form shape, taken
  // straight off a row the caller is still rendering, so the form has to work on a copy.
  const [initialValues] = useState<ProceduresPickerFormType>(() => {
    if (!editTarget) return proceduresPickerInitialValues;

    return editTarget.lockedSide === "diagnosis"
      ? getProceduresPickerInitialValues(editTarget.procedures, [editTarget.condition])
      : structuredClone({
          procedures: [editTarget.procedure],
          conditions: editTarget.conditions,
        });
  });

  // No validate: the save button is gated by resolveCanSave below, which is the only rule for
  // what a session needs before it can be saved.
  const form = useForm<ProceduresPickerFormType>({ initialValues });

  const hasConditions = form.values.conditions.length > 0;
  const hasProcedures = form.values.procedures.length > 0;

  // Compared against the mount snapshot rather than through form.isDirty(): Mantine's dirty map
  // only tracks the fields edited through setFieldValue, so a note typed and then undone would hide
  // procedures added or removed alongside it.
  const hasChanges = !isEqual(form.values, initialValues);

  const resolveSaveIntent = (): SaveIntent => {
    if (isEditingProcedureCode) return "updateProcedureCode";
    if (isEditingDiagnosis) return "update";

    return hasProcedures ? "add" : "addDiagnoses";
  };

  const { buttonLabel, successTitle, successMessage } =
    buildSaveCopy(stagedSaveLabel)[resolveSaveIntent()];

  // Adding needs a diagnosis, and a procedure as well when the caller requires one. A
  // diagnosis-locked session already has its diagnosis, so it only needs an edit to the procedures.
  // A procedure-locked session always keeps its procedure, so it needs an edit plus at least one
  // diagnosis left to bill the code under.
  const resolveCanSave = (): boolean => {
    if (isEditingProcedureCode) return hasChanges && hasConditions;
    if (isEditingDiagnosis) return hasChanges;

    return hasConditions && (hasProcedures || !requiresProcedure);
  };

  const canSave = resolveCanSave();

  // Hands the session to the tab and closes. Nothing is written here and nothing can fail, so the
  // provider keeps working. The encounter's Save or Sign & Lock is what reaches the server.
  const stageOnEncounter = () => {
    onStage(buildStagedProceduresOperation(form.values, initialValues));

    toast({
      title: successTitle,
      message: successMessage,
      type: "success",
      autoClose: 3000,
    });

    onClose();
  };

  return (
    <>
      <Flex
        flex={1}
        bd="1px solid var(--mantine-color-cream-4)"
        style={{ overflow: "hidden", borderRadius: 12 }}
      >
        <ProceduresPickerLeftPanel
          form={form}
          serviceDate={serviceDate}
          conditionsLocked={isEditingDiagnosis}
          proceduresLocked={isEditingProcedureCode}
        />
        <ProceduresPickerRightPanel
          form={form}
          serviceDate={serviceDate}
          conditionsLocked={isEditingDiagnosis}
          proceduresLocked={isEditingProcedureCode}
        />
      </Flex>

      <Group pt="md" justify="space-between">
        <CptCopyrightNotice />
        <Group>
          <Button
            color="dark"
            variant="outline"
            onClick={onClose}
            data-testid="procedures-picker-cancel-button"
          >
            Cancel
          </Button>
          <Button
            color="burgundy.9"
            disabled={!canSave}
            onClick={stageOnEncounter}
            data-testid="procedures-picker-save-button"
          >
            {buttonLabel}
          </Button>
        </Group>
      </Group>
    </>
  );
}
